/**
 * **分岐レイヤー**（Issue #589 → #628）。core の生イベントを**全部包み**、テーマが実装する単位＝**状況**を定義し直す。
 * **テーマは core のイベントを知らない**（ユーザー判断 2026-09-30）。テーマが名乗るのは、ここが付けた状況の名前だけ。
 *
 * - 状況の名前は**平らな 1 段**（`CATALOG` のキー）。分岐のあるイベントは `<core のイベント名>_<variant>`
 *   （`move_destroy` / `damage_battle` / `new_phase_battle`）、分岐の無いイベントは core と同じ名前（`new_turn` / `draw`）。
 *   core のイベント名と同じ名前でもよい（名前を決めるのはここ）
 * - 1 生イベント = 1 ファイル。`{ event, variants: [{ name, label }], classify(ctx) → { variant, ...事実 } | null }`。
 *   分岐の無いイベントは `variants` が空で、`classify` は事実だけを載せる（無ければ `PASSTHROUGH` に名前を並べるだけ）
 * - `classify` は `opens` と同じ context（`payload` / `board` / `boardBefore` / `history`）を受け取り、
 *   **判定に使った事実**（移動前後の姿・戦闘の相手など）を payload に載せて返す。
 *   **null なら「見せる状況ではない」**（どのテーマでも帯を作らない）
 * - **未来は見ない。過去は窓つきの `history` だけ**（R11）。テーマは `history` を読まない（#564）
 * - **ここに無い core のイベントはテーマから書けない**（`situate` が null）。演出を作るときにここへ足す
 */
import attack from './attack';
import battle from './battle';
import becomeTarget from './become_target';
import chainSolving from './chain_solving';
import chained from './chained';
import chaining from './chaining';
import damage from './damage';
import draw from './draw';
import flipsummoning from './flipsummoning';
import materialSummon from './material_summon';
import move from './move';
import newPhase from './new_phase';
import posChange from './pos_change';
import spsummoning from './spsummoning';
import start from './start';
import statChange from './stat_change';

/**
 * **分岐も事実も持たないイベント**（payload をそのまま状況にする）。状況の名前は core と同じ。
 * 判定や事実が要るようになったら、ファイルに出して上の一覧へ移す
 */
export const PASSTHROUGH = Object.freeze({
  attack_disabled: '攻撃の無効',
  chain_disabled: '効果の無効',
  chain_end: 'チェーンの終わり',
  chain_negated: '発動の無効',
  chain_solved: 'チェーンのリンクの処理の終わり',
  confirm_cards: 'カードの確認',
  confirm_decktop: 'デッキの上をめくる',
  damage_step_end: 'ダメージステップの終わり',
  damage_step_start: 'ダメージステップの始まり',
  flipsummoned: '反転召喚の成功',
  new_turn: 'ターンの始まり',
  pay_lpcost: 'LP を払うコスト',
  recover: 'LP の回復',
  set: 'セット',
  shuffle_deck: 'デッキのシャッフル',
  shuffle_hand: '手札のシャッフル',
  spsummoned: '特殊召喚の成功',
  summoned: '召喚の成功',
  summoning: '召喚（宣言）',
  win: '決着',
});

const passthrough = (event, label) => Object.freeze({
  event, label, variants: Object.freeze([]), classify: ({ payload }) => (payload ? { ...payload } : null),
});

const LIST = Object.freeze([
  attack, battle, becomeTarget, chainSolving, chained, chaining, damage, draw, flipsummoning, materialSummon, move, newPhase, posChange, spsummoning, start, statChange,
  ...Object.entries(PASSTHROUGH).map(([event, label]) => passthrough(event, label)),
]);

/** core のイベント名 → 定義 */
export const SITUATIONS = Object.freeze(Object.fromEntries(LIST.map((s) => [s.event, s])));

/**
 * 状況の名前（分岐のあるイベントは `<event>_<variant>`、無ければ `<event>`）。
 * **`_motion` で終わる名前は不可**（motion の帯の型 `<situation>_motion` と読み分けられなくなる）
 */
export const situationId = (event, variant = null) => {
  const id = variant ? `${event}_${variant}` : String(event);
  if (id.endsWith('_motion')) throw new Error(`situation ${id}: _motion で終わる名前は使えません（motion の帯の型と紛れる）`);
  return id;
};

/**
 * **状況のカタログ**（状況の名前 → `{ id, label, event, variant }`）。テーマが実装できる単位の一覧。
 * `/fx-samples` のような一覧・テーマの検め（`checkThemeSituations`）はここを見る
 */
const catalogEntries = LIST.flatMap((def) => (def.variants.length
  ? def.variants.map((v) => [situationId(def.event, v.name), Object.freeze({ id: situationId(def.event, v.name), label: v.label, event: def.event, variant: v.name })])
  : [[situationId(def.event), Object.freeze({ id: def.event, label: def.label ?? def.event, event: def.event, variant: null })]]));
// **平らな名前は衝突しうる**（`a_b` の variant `c` と `a` の variant `b_c`）。黙って上書きせず落とす
catalogEntries.forEach(([id], i) => {
  if (catalogEntries.findIndex(([other]) => other === id) !== i) throw new Error(`situation ${id}: 名前が衝突しています`);
});
export const CATALOG = Object.freeze(Object.fromEntries(catalogEntries));

/** そのイベントの状況の名前（無ければ空） */
export const situationsOf = (event) => Object.freeze(Object.values(CATALOG).filter((s) => s.event === event).map((s) => s.id));

/**
 * **生イベントの payload → 状況つきの payload**（`situation` に状況の名前、`variant` にその短い名前）。
 * 定義の無いイベントは null（テーマから書けない）。定義があって null なら見せる状況ではない（呼ぶ側は帯を作らない）。
 * 返した `variant` が宣言に無ければ例外（分岐レイヤー自身の綴り間違いを黙らせない）
 */
export function situate(event, ctx) {
  const def = SITUATIONS[event];
  if (!def) return null;
  const out = def.classify(ctx);
  if (out == null) return null;
  const names = def.variants.map((v) => v.name);
  const { variant, ...rest } = out;
  if (names.length) {
    if (!names.includes(variant)) throw new Error(`situation ${event}: variant ${variant} は宣言にありません（${names.join(' / ')}）`);
  } else if (variant !== undefined) {
    throw new Error(`situation ${event}: 分岐の無いイベントが variant を返しました`);
  }
  // `situation` が状況の名前。`variant` はその短い名前（分岐の無いイベントは null。テストや一覧が読む）
  return { ...rest, situation: situationId(event, names.length ? variant : null), variant: names.length ? variant : null };
}

/**
 * **テーマの演出が名乗る状況を検める**（`duelSpecs` が起動時に呼ぶ）。
 * カタログに無い状況を名乗ったファイルは「置いたのに出ない」になるので、黙らせず例外にする
 */
export function checkThemeSituations(theme) {
  (theme?.effects ?? []).forEach((e) => {
    if (!CATALOG[e?.situation]) {
      throw new Error(`theme ${theme?.name}: 状況 ${e?.situation} はカタログにありません（editorPorts/duelSituations/）`);
    }
  });
}
